/**
 * Per-user theme, timezone and date/time formatting preferences.
 *
 * Preferences live in DynamoDB so they follow a user between machines, but the
 * theme must be applied before the first paint or the page flashes the default
 * theme while the request is in flight. The provider therefore keeps a
 * localStorage mirror: it applies the cached values synchronously on mount,
 * then reconciles with the server and updates the cache.
 *
 * A failure to load preferences never blocks the app. The user still gets a
 * usable page on cached or default settings, with the error surfaced on the
 * settings screen rather than as a full-page crash.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  fetchPreferences,
  savePreferences,
  type UserPreferences,
} from "../api/client";
import { DEFAULT_THEME, applyTheme, isThemeId, type ThemeId } from "../theme/themes";
import {
  AUTO_TIMEZONE,
  detectTimezone,
  resolveTimezone,
  type DateFormatId,
  type FormatContext,
  type TimeFormatId,
} from "../utils/datetime";

const STORAGE_KEY = "pbi-sox.preferences.v1";

/** Beyond this, a client clock is wrong enough to make relative times misleading. */
const CLOCK_SKEW_WARN_MS = 5 * 60 * 1000;

export interface Preferences {
  theme: ThemeId;
  /** May be the literal "auto", meaning "follow the browser". */
  timezone: string;
  dateFormat: DateFormatId;
  timeFormat: TimeFormatId;
}

const DEFAULTS: Preferences = {
  theme: DEFAULT_THEME,
  timezone: AUTO_TIMEZONE,
  dateFormat: "iso",
  timeFormat: "24h",
};

interface PreferencesContextValue {
  preferences: Preferences;
  /** `timezone` with "auto" resolved to a concrete IANA zone. */
  effectiveTimezone: string;
  /** Ready-made context for the formatters in utils/datetime. */
  format: FormatContext;
  /** True until the first server response (or its failure) has been handled. */
  loading: boolean;
  /** Set when preferences could not be loaded or saved. */
  error: string | null;
  /** Non-null when the browser clock disagrees materially with the server. */
  clockSkewMs: number | null;
  update: (patch: Partial<Preferences>) => Promise<void>;
  reset: () => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function coerce(raw: Partial<Record<keyof Preferences, unknown>>): Preferences {
  const theme = isThemeId(raw.theme) ? raw.theme : DEFAULTS.theme;

  const timezoneRaw = typeof raw.timezone === "string" ? raw.timezone : DEFAULTS.timezone;
  const timezone =
    timezoneRaw === AUTO_TIMEZONE || resolveTimezone(timezoneRaw) === timezoneRaw
      ? timezoneRaw
      : DEFAULTS.timezone;

  const dateFormat = (["iso", "dmy", "mdy", "medium"] as const).includes(
    raw.dateFormat as DateFormatId
  )
    ? (raw.dateFormat as DateFormatId)
    : DEFAULTS.dateFormat;

  const timeFormat = (["24h", "12h"] as const).includes(raw.timeFormat as TimeFormatId)
    ? (raw.timeFormat as TimeFormatId)
    : DEFAULTS.timeFormat;

  return { theme, timezone, dateFormat, timeFormat };
}

function readCache(): Preferences {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return coerce(JSON.parse(raw));
  } catch {
    // Corrupt cache, or storage blocked in a hardened browser profile.
    return DEFAULTS;
  }
}

function writeCache(prefs: Preferences): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Private browsing or a full quota. Preferences still work for this
    // session and remain saved server-side; only the pre-paint hint is lost.
  }
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  // Initialised from cache so the very first paint already uses the right theme.
  const [preferences, setPreferences] = useState<Preferences>(readCache);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clockSkewMs, setClockSkewMs] = useState<number | null>(null);

  // Guards against a late response from a previous save overwriting a newer edit.
  const saveSeq = useRef(0);

  // Apply the theme synchronously on every change, before paint.
  useEffect(() => {
    applyTheme(preferences.theme);
    writeCache(preferences);
  }, [preferences]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const remote = await fetchPreferences();
        if (cancelled) return;

        setPreferences(coerce(remote));

        const serverTime = Date.parse(remote.serverTime);
        if (!Number.isNaN(serverTime)) {
          const skew = Date.now() - serverTime;
          setClockSkewMs(Math.abs(skew) > CLOCK_SKEW_WARN_MS ? skew : null);
        }
        setError(null);
      } catch (err) {
        if (cancelled) return;
        // Deliberately non-fatal: a settings lookup must not take down an
        // audit dashboard. Cached or default values are already applied.
        setError(
          "Could not load your saved settings, so defaults are being used. Changes may not persist."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback(async (next: Preferences) => {
    const seq = ++saveSeq.current;
    // Apply optimistically: the theme should switch the instant it is clicked.
    setPreferences(next);
    try {
      await savePreferences(next as UserPreferences);
      if (seq === saveSeq.current) setError(null);
    } catch {
      if (seq === saveSeq.current) {
        setError("Your settings were applied for this session but could not be saved.");
      }
    }
  }, []);

  const update = useCallback(
    (patch: Partial<Preferences>) =>
      persist(coerce({ ...preferences, ...patch })),
    [persist, preferences]
  );

  const reset = useCallback(() => persist(DEFAULTS), [persist]);

  const effectiveTimezone = useMemo(
    () => resolveTimezone(preferences.timezone),
    [preferences.timezone]
  );

  const format = useMemo<FormatContext>(
    () => ({
      timezone: effectiveTimezone,
      dateFormat: preferences.dateFormat,
      timeFormat: preferences.timeFormat,
    }),
    [effectiveTimezone, preferences.dateFormat, preferences.timeFormat]
  );

  const value = useMemo<PreferencesContextValue>(
    () => ({
      preferences,
      effectiveTimezone,
      format,
      loading,
      error,
      clockSkewMs,
      update,
      reset,
    }),
    [preferences, effectiveTimezone, format, loading, error, clockSkewMs, update, reset]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error("usePreferences must be used inside a PreferencesProvider");
  }
  return ctx;
}

/**
 * Just the formatting context, for the many components that only render dates.
 */
export function useDateFormat(): FormatContext {
  return usePreferences().format;
}

/** The detected browser timezone, for showing what "Automatic" resolves to. */
export function useDetectedTimezone(): string {
  return useMemo(() => detectTimezone(), []);
}
